import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  Float32BufferAttribute,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Le char est entierement genere par le code : aucun fichier a charger.
 *
 * C'est le choix inverse de celui de la licorne du projet jumeau, qui partait
 * d'un glTF anime par morph targets. Un char n'a pas de deformation souple :
 * c'est une caisse, une tourelle qui pivote, un canon qui pointe et deux
 * chenilles qui defilent. Quatre corps rigides, donc quatre Object3D emboites,
 * et le modele devient un reglage de cotes plutot qu'un asset a telecharger.
 * cf. doc/05-char-et-chenilles.md
 */

/**
 * Cotes du vehicule, en metres. Elles sont de l'ordre de celles d'un char de
 * bataille moderne (7,4 m de coque, 3,6 m de large, 3 m de haut) parce que
 * c'est ce qui donne la bonne echelle face a un homme, a un fut de 200 litres
 * et a une portee de 250 m.
 *
 * L'origine du char est au sol, au centre de la caisse : c'est le point qu'on
 * pose sur le terrain.
 */
export const DIMS = {
  /** Longueur hors tout de la chenille. */
  trackLength: 7.4,
  /** Rayon du barbotin et du galet tendeur ; la chenille fait 2x ce rayon. */
  trackRadius: 0.5,
  trackHalfWidth: 0.33,
  /** Ecartement des deux chenilles, du centre du char a l'axe d'une chenille. */
  trackOffsetX: 1.49,

  /** Hauteur du toit de caisse : c'est la que tourne la tourelle. */
  hullTop: 1.78,

  /** Centre de la tourelle, en z. Legerement en arriere du milieu. */
  turretZ: -0.45,
  /** Axe de pointage du canon (tourillon), dans le repere de la tourelle. */
  trunnionY: 2.22,
  trunnionZ: 0.6,
  /** Distance du tourillon a la bouche, le long du tube. */
  barrelLength: 5.0,
} as const;

/** Nombre de galets de roulement par chenille. */
const ROAD_WHEELS = 7;

/** Epaisseur du patin de chenille, en metres. */
const BELT_THICKNESS = 0.1;

/**
 * Caisse : plancher, sponsons, glacis incline, plaque arriere et jupes.
 *
 * Tout est fusionne en une seule geometrie. Ces pieces ne bougent jamais les
 * unes par rapport aux autres : les garder separees couterait huit draw calls
 * pour rien. cf. doc/06-boucle-de-jeu-et-perf.md
 */
export function buildHull(): BufferGeometry {
  const parts: BufferGeometry[] = [
    // Coque basse, entre les chenilles.
    new BoxGeometry(2.5, 0.62, 6.4).translate(0, 0.81, 0),
    // Sponsons : la caisse deborde au-dessus des chenilles.
    new BoxGeometry(3.58, 0.66, 7.0).translate(0, 1.45, -0.1),
    // Plaque arriere.
    new BoxGeometry(3.5, 1.0, 0.24).translate(0, 1.25, -3.52),
    // Panier de rangement a l'arriere.
    new BoxGeometry(2.6, 0.5, 0.5).translate(0, 1.55, -3.85),
  ];

  // Glacis : la plaque avant inclinee. On la place en resolvant la geometrie
  // plutot qu'en tatonnant sur un angle -- elle doit joindre exactement le bas
  // de la caisse a l'avant et le toit en arriere.
  const from = { x: 3.2, y: 0.5 };
  const to = { x: 1.3, y: DIMS.hullTop };
  const dz = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dz, dy);
  parts.push(
    new BoxGeometry(3.5, len, 0.24)
      // Un BoxGeometry a son axe long selon Y ; Rx(t) envoie +Y sur
      // (0, cos t, sin t), donc t = atan2(dz, dy) aligne la plaque.
      .rotateX(Math.atan2(dz, dy))
      .translate(0, (from.y + to.y) / 2, (from.x + to.x) / 2),
  );

  // Jupes laterales : elles couvrent le haut des chenilles. Sans elles on voit
  // le brin superieur de la chenille flotter dans le vide.
  for (const side of [-1, 1]) {
    parts.push(
      new BoxGeometry(0.12, 0.74, 6.5).translate(side * (DIMS.trackOffsetX + 0.36), 1.3, -0.1),
    );
    // Garde-boue avant.
    parts.push(
      new BoxGeometry(0.76, 0.14, 0.7).translate(side * DIMS.trackOffsetX, 1.72, 3.4),
    );
    // Pots d'echappement, sur le capot moteur.
    parts.push(
      new CylinderGeometry(0.17, 0.17, 0.8, 8)
        .rotateZ(Math.PI / 2)
        .translate(side * 1.1, 1.86, -3.1),
    );
  }

  return mergeGeometries(parts, false)!;
}

/**
 * Tourelle : masse principale facettee, masque de canon, soute arriere,
 * coupole, trappe, lance-pots fumigenes et antenne.
 *
 * La masse est un cylindre a huit pans mis a l'echelle plutot qu'une boite :
 * les pans coupes se lisent comme du blindage, et une boite se lit comme un
 * carton.
 */
export function buildTurret(): BufferGeometry {
  const body = new CylinderGeometry(1.0, 1.24, 0.84, 8, 1)
    // Un pan plein face a l'avant plutot qu'une arete.
    .rotateY(Math.PI / 8)
    .scale(1.25, 1, 1.42);

  const parts: BufferGeometry[] = [
    body.translate(0, 0.42, 0),
    // Masque de canon : la piece epaisse qui entoure le tourillon.
    new BoxGeometry(1.15, 0.72, 0.95).translate(0, DIMS.trunnionY - DIMS.hullTop, 1.18),
    // Soute arriere.
    new BoxGeometry(2.0, 0.52, 0.95).translate(0, 0.46, -1.52),
    // Coupole du chef de char, a droite.
    new CylinderGeometry(0.42, 0.45, 0.3, 8).translate(0.58, 0.97, -0.25),
    // Trappe du chargeur, a gauche.
    new CylinderGeometry(0.36, 0.36, 0.1, 8).translate(-0.56, 0.87, -0.3),
    // Mitrailleuse de toit.
    new CylinderGeometry(0.05, 0.05, 1.0, 6).rotateX(Math.PI / 2).translate(0.58, 1.2, 0.25),
    // Antenne : presque invisible de pres, mais c'est elle qui donne sa
    // silhouette au char a contre-jour.
    new CylinderGeometry(0.025, 0.035, 1.9, 5).translate(-0.95, 1.6, -1.3),
  ];

  for (const side of [-1, 1]) {
    // Lance-pots fumigenes, en grappe de trois.
    for (let i = 0; i < 3; i++) {
      parts.push(
        new CylinderGeometry(0.075, 0.075, 0.3, 6)
          .rotateX(-0.35)
          .translate(side * 1.12, 0.58 + i * 0.17, -0.95 + i * 0.05),
      );
    }
  }

  return mergeGeometries(parts, false)!;
}

/**
 * Tube de canon, axe sur +z, tourillon a l'origine.
 *
 * Le frein de bouche n'est pas un detail gratuit : c'est le reperé visuel qui
 * dit ou est la bouche, donc ou naissent l'eclair et l'obus.
 */
export function buildBarrel(): BufferGeometry {
  const L = DIMS.barrelLength;
  const parts: BufferGeometry[] = [
    // Culasse, derriere le tourillon.
    new CylinderGeometry(0.21, 0.21, 0.8, 10).rotateX(Math.PI / 2).translate(0, 0, -0.3),
    // Tube, legerement tronconique.
    new CylinderGeometry(0.105, 0.125, L - 0.45, 12)
      .rotateX(Math.PI / 2)
      .translate(0, 0, (L - 0.45) / 2),
    // Manchon thermique sur la premiere moitie.
    new CylinderGeometry(0.15, 0.15, 2.1, 12).rotateX(Math.PI / 2).translate(0, 0, 1.5),
    // Frein de bouche, et sa gorge.
    new CylinderGeometry(0.17, 0.17, 0.42, 12).rotateX(Math.PI / 2).translate(0, 0, L - 0.21),
    new CylinderGeometry(0.125, 0.125, 0.14, 12).rotateX(Math.PI / 2).translate(0, 0, L - 0.5),
  ];
  return mergeGeometries(parts, false)!;
}

/**
 * Galets de roulement, barbotin et tendeur, en une geometrie.
 *
 * Le galet est *plus large* que la chenille (0,76 m contre 0,66) : c'est ce
 * qui le rend visible de profil, la ou la chenille passe autour de lui. Un
 * galet plus etroit que la chenille serait entierement cache a l'interieur de
 * l'anneau, et le char roulerait sur des roues invisibles.
 *
 * Le moyeu clair et les deux temoins de rayon sont indispensables : un
 * cylindre uni qui tourne ne se voit pas tourner.
 */
export function buildWheel(): BufferGeometry {
  const parts: BufferGeometry[] = [
    // Bande de roulement. Geometrie de rayon 1 : l'echelle par instance donne
    // son rayon a chaque galet, sans toucher a sa largeur.
    new CylinderGeometry(1, 1, 0.76, 14).rotateZ(Math.PI / 2),
    new CylinderGeometry(0.42, 0.42, 0.82, 10).rotateZ(Math.PI / 2),
  ];
  for (const face of [-1, 1]) {
    parts.push(new BoxGeometry(0.05, 1.68, 0.17).translate(face * 0.39, 0, 0));
  }
  return mergeGeometries(parts, false)!;
}

export interface WheelStation {
  z: number;
  /** Hauteur de l'axe : le galet roule sur la face interieure de la chenille. */
  y: number;
  radius: number;
}

/**
 * Position de chaque galet. Le barbotin (avant) et le tendeur (arriere) sont
 * centres sur les demi-cercles de la chenille : c'est eux qui en portent la
 * courbure, leur rayon est donc impose par celui du contour.
 */
export function wheelStations(): WheelStation[] {
  const halfRun = DIMS.trackLength / 2 - DIMS.trackRadius;
  const out: WheelStation[] = [];
  for (let i = 0; i < ROAD_WHEELS; i++) {
    const t = i / (ROAD_WHEELS - 1);
    // Le galet repose sur la face interieure du brin inferieur, a
    // BELT_THICKNESS du sol : sans ce decalage il traverse sa propre chenille.
    out.push({ z: -halfRun * 0.9 + t * halfRun * 1.8, y: 0.38 + BELT_THICKNESS, radius: 0.38 });
  }
  const end = DIMS.trackRadius - BELT_THICKNESS;
  out.push({ z: halfRun, y: DIMS.trackRadius, radius: end });
  out.push({ z: -halfRun, y: DIMS.trackRadius, radius: end });
  return out;
}

interface Station {
  z: number;
  y: number;
  /** Normale sortante, dans le plan (z, y). */
  nz: number;
  ny: number;
  /** Abscisse curviligne depuis le depart du contour. */
  arc: number;
}

/**
 * Contour ferme de la chenille, dans le plan (z, y) : deux brins droits et
 * deux demi-cercles. C'est le profil exact d'une chenille tendue sur un
 * barbotin et un tendeur de meme diametre.
 */
function beltPath(length: number, radius: number, arcSteps: number): Station[] {
  const halfRun = length / 2 - radius;
  const out: Station[] = [];

  const push = (z: number, y: number, nz: number, ny: number): void => {
    const prev = out[out.length - 1];
    const arc = prev ? prev.arc + Math.hypot(z - prev.z, y - prev.y) : 0;
    out.push({ z, y, nz, ny, arc });
  };

  // Brin inferieur, d'arriere en avant : normale vers le bas.
  push(-halfRun, 0, 0, -1);
  push(halfRun, 0, 0, -1);
  // Demi-cercle avant, de bas en haut.
  for (let i = 1; i <= arcSteps; i++) {
    const a = -Math.PI / 2 + (Math.PI * i) / arcSteps;
    push(halfRun + radius * Math.cos(a), radius + radius * Math.sin(a), Math.cos(a), Math.sin(a));
  }
  // Brin superieur, d'avant en arriere.
  push(-halfRun, radius * 2, 0, 1);
  // Demi-cercle arriere : on s'arrete avant 270 degres, le point final est
  // deja la station 0 et la boucle se referme dessus.
  for (let i = 1; i < arcSteps; i++) {
    const a = Math.PI / 2 + (Math.PI * i) / arcSteps;
    push(-halfRun + radius * Math.cos(a), radius + radius * Math.sin(a), Math.cos(a), Math.sin(a));
  }
  return out;
}

export interface Belt {
  geometry: BufferGeometry;
  /** Longueur de monde couverte par une repetition de la texture, en metres. */
  tile: number;
  /** Perimetre du contour, en metres. */
  perimeter: number;
}

/**
 * Chenille : le contour est extrude en largeur *et* en epaisseur, ce qui donne
 * un vrai anneau a section rectangulaire plutot qu'un ruban sans epaisseur.
 *
 * C'est necessaire parce que la vue la plus fréquente d'un char est de profil :
 * un ruban d'epaisseur nulle y disparaitrait.
 *
 * La texture est repetee un nombre *entier* de fois sur le perimetre. Sans ca,
 * la couture du contour tombe au milieu d'un maillon et la chenille a un
 * maillon faux qui tourne avec elle.
 */
export function buildTrackBelt(targetTile = 2.0): Belt {
  const stations = beltPath(DIMS.trackLength, DIMS.trackRadius, 10);
  const n = stations.length;
  const hw = DIMS.trackHalfWidth;
  const thickness = BELT_THICKNESS;

  const last = stations[n - 1]!;
  const first = stations[0]!;
  const perimeter = last.arc + Math.hypot(first.z - last.z, first.y - last.y);
  const repeats = Math.max(1, Math.round(perimeter / targetTile));
  const tile = perimeter / repeats;

  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  /** Quatre sommets par station : exterieur gauche/droit, interieur idem. */
  for (const s of stations) {
    const oy = s.y;
    const oz = s.z;
    const iy = s.y - s.ny * thickness;
    const iz = s.z - s.nz * thickness;
    const v = s.arc / tile;

    // 0: exterieur -x, 1: exterieur +x, 2: interieur +x, 3: interieur -x
    positions.push(-hw, oy, oz, hw, oy, oz, hw, iy, iz, -hw, iy, iz);
    normals.push(0, s.ny, s.nz, 0, s.ny, s.nz, 0, -s.ny, -s.nz, 0, -s.ny, -s.nz);
    // u : la largeur du patin sur la face de roulement, une bande etroite sur
    // les flancs -- les maillons y apparaissent de profil, ce qui est juste.
    uvs.push(0, v, 1, v, 1.3, v, -0.3, v);
  }

  // Quatre faces entre chaque paire de stations ; la derniere se referme sur
  // la premiere, d'ou le modulo.
  for (let i = 0; i < n; i++) {
    const a = i * 4;
    const b = ((i + 1) % n) * 4;
    const quad = (p: number, q: number, r: number, s: number): void => {
      indices.push(p, q, r, p, r, s);
    };
    quad(a + 0, a + 1, b + 1, b + 0); // face de roulement
    quad(a + 2, a + 3, b + 3, b + 2); // face interieure
    quad(a + 1, a + 2, b + 2, b + 1); // flanc +x
    quad(a + 3, a + 0, b + 0, b + 3); // flanc -x
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  // Les normales des flancs sont approximees par celles du contour ; on les
  // recalcule depuis la geometrie, qui a raison.
  geo.computeVertexNormals();
  geo.computeBoundingSphere();

  return { geometry: geo, tile, perimeter };
}
